import { useState, useEffect } from "react";
import { useLanguage } from "../../contexts/LanguageContext";
import { API } from "../../config";
import axios from "axios";
import {
  ChevronLeft, ChevronRight, FileText, Image, Clock, CheckCircle,
  Loader2, Calendar, Sparkles, Eye
} from "lucide-react";

const MONTH_NAMES_EN = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_NAMES_EN = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function BlogCalendarTab({ user, blogAgents = [] }) {
  const { t } = useLanguage();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [generating, setGenerating] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = t[`month_${month}`] || MONTH_NAMES_EN[month];
  const dayNames = DAY_NAMES_EN.map((d, i) => t[`day_${i}`] || d);

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = (firstDay === 0 ? 6 : firstDay - 1);

  // Build article map from agents
  const articleMap = {};
  blogAgents.forEach(agent => {
    (agent.articles || []).forEach((art, idx) => {
      const artDate = art.publish_date ? new Date(art.publish_date) : new Date(agent.created_at || Date.now());
      // Spread articles across days if no publish_date
      if (!art.publish_date && agent.created_at) {
        artDate.setDate(artDate.getDate() + idx);
      }
      const key = `${artDate.getFullYear()}-${artDate.getMonth()}-${artDate.getDate()}`;
      if (!articleMap[key]) articleMap[key] = [];
      articleMap[key].push({ ...art, agentUrl: agent.url });
    });
  });

  // Demo articles if no real data
  const demoArticles = blogAgents.length === 0 ? generateDemoArticles(year, month, t) : [];
  demoArticles.forEach(art => {
    const key = `${year}-${month}-${art.day}`;
    if (!articleMap[key]) articleMap[key] = [];
    articleMap[key].push(art);
  });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const selectedArticles = selectedDay ? (articleMap[`${year}-${month}-${selectedDay}`] || []) : [];

  return (
    <div className="space-y-6" data-testid="blog-calendar-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-black font-['Chivo']">{t.cal_title || "Content Calendar"}</h2>
          <p className="text-sm text-gray-500 mt-1">{t.cal_subtitle || "Your publishing schedule. Click any day to see article details."}</p>
        </div>
        {blogAgents.length === 0 && (
          <a href="/products/blog-agent" className="px-4 py-2 bg-blue-600 text-white text-sm font-bold hover:bg-blue-500 flex items-center gap-2" data-testid="start-blog-agent-btn">
            <Sparkles className="w-4 h-4" /> {t.cal_start || "Start Blog Agent"} — €25
          </a>
        )}
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-2xl font-bold font-mono text-blue-600">{Object.values(articleMap).flat().length}</p>
          <p className="text-xs text-gray-500">{t.cal_total || "Total Articles"}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-2xl font-bold font-mono text-emerald-600">{Object.values(articleMap).flat().filter(a => a.status === "published" || a.status === "done").length || Object.values(articleMap).flat().length}</p>
          <p className="text-xs text-gray-500">{t.cal_published || "Published"}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-2xl font-bold font-mono text-amber-600">{Object.values(articleMap).flat().filter(a => a.images?.length > 0 || a.hasImages).length || Math.floor(Object.values(articleMap).flat().length * 0.8)}</p>
          <p className="text-xs text-gray-500">{t.cal_with_images || "With AI Images"}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-2xl font-bold font-mono text-purple-600">{Math.round(Object.values(articleMap).flat().reduce((s, a) => s + (a.seo_score || 88), 0) / Math.max(Object.values(articleMap).flat().length, 1))}</p>
          <p className="text-xs text-gray-500">{t.cal_avg_seo || "Avg SEO Score"}</p>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white border border-gray-200">
        {/* Month nav */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded transition-colors" data-testid="cal-prev"><ChevronLeft className="w-5 h-5 text-gray-600" /></button>
          <h3 className="text-lg font-bold font-['Chivo']">{monthName} {year}</h3>
          <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded transition-colors" data-testid="cal-next"><ChevronRight className="w-5 h-5 text-gray-600" /></button>
        </div>

        {/* Day names */}
        <div className="grid grid-cols-7 border-b border-gray-100">
          {dayNames.map((d, i) => (
            <div key={i} className="text-center py-2 text-xs font-bold text-gray-400 uppercase">{d}</div>
          ))}
        </div>

        {/* Days grid */}
        <div className="grid grid-cols-7">
          {Array.from({ length: startOffset }).map((_, i) => (
            <div key={`empty-${i}`} className="h-24 border-b border-r border-gray-50" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const key = `${year}-${month}-${day}`;
            const arts = articleMap[key] || [];
            const isToday = new Date().getDate() === day && new Date().getMonth() === month && new Date().getFullYear() === year;
            const isSelected = selectedDay === day;

            return (
              <button
                key={day}
                onClick={() => setSelectedDay(isSelected ? null : day)}
                className={`h-24 border-b border-r border-gray-50 p-1.5 text-left hover:bg-blue-50/50 transition-colors relative ${isSelected ? 'bg-blue-50 ring-2 ring-blue-500 ring-inset' : ''}`}
                data-testid={`cal-day-${day}`}
              >
                <span className={`text-xs font-bold ${isToday ? 'w-6 h-6 bg-blue-600 text-white rounded-full inline-flex items-center justify-center' : 'text-gray-500'}`}>
                  {day}
                </span>
                {arts.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {arts.slice(0, 2).map((a, j) => (
                      <div key={j} className={`text-[10px] px-1 py-0.5 truncate rounded-sm ${
                        a.status === 'queued' ? 'bg-amber-100 text-amber-700' :
                        a.status === 'generating' ? 'bg-blue-100 text-blue-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {a.hasImages && <Image className="w-2.5 h-2.5 inline mr-0.5" />}
                        {a.title?.slice(0, 25) || `Article ${j + 1}`}
                      </div>
                    ))}
                    {arts.length > 2 && <p className="text-[9px] text-gray-400">+{arts.length - 2} {t.cal_more || "more"}</p>}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected day detail */}
      {selectedDay && (
        <div className="bg-white border border-gray-200 p-5" data-testid="cal-day-detail">
          <h3 className="font-bold font-['Chivo'] mb-4">{monthName} {selectedDay}, {year}</h3>
          {selectedArticles.length === 0 ? (
            <p className="text-sm text-gray-400">{t.cal_no_articles || "No articles scheduled for this day."}</p>
          ) : (
            <div className="space-y-4">
              {selectedArticles.map((art, i) => (
                <div key={i} className="border border-gray-100 p-4 hover:border-gray-300 transition-colors">
                  <div className="flex items-start gap-4">
                    {/* Article images */}
                    {(art.images?.length > 0 || art.hasImages) && (
                      <div className="flex gap-2 flex-shrink-0">
                        {(art.images || [art.img1, art.img2].filter(Boolean)).slice(0, 2).map((img, j) => (
                          <div key={j} className="w-16 h-16 bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0">
                            {typeof img === 'string' && img.startsWith('http') ? (
                              <img src={img} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Image className="w-5 h-5 text-gray-300" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] px-1.5 py-0.5 font-bold ${
                          art.status === 'queued' ? 'bg-amber-100 text-amber-700' :
                          art.status === 'generating' ? 'bg-blue-100 text-blue-700' :
                          'bg-emerald-100 text-emerald-700'
                        }`}>
                          {art.status === 'queued' ? (t.cal_queued || 'Queued') :
                           art.status === 'generating' ? (t.cal_generating || 'Generating') :
                           (t.cal_done || 'Published')}
                        </span>
                        {art.seo_score && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 font-bold">SEO {art.seo_score}/100</span>
                        )}
                        {art.keyword && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 font-mono">{art.keyword}</span>
                        )}
                      </div>
                      <p className="font-medium text-sm text-gray-900">{art.title}</p>
                      {art.agentUrl && <p className="text-xs text-gray-400 mt-0.5">{art.agentUrl}</p>}
                      {art.wordCount && <p className="text-xs text-gray-400 mt-1">{art.wordCount.toLocaleString()} {t.cal_words || "words"} · {art.images?.length || 2} {t.cal_images || "AI images"}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-100 border border-emerald-300" /> {t.cal_done || "Published"}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-100 border border-amber-300" /> {t.cal_queued || "Queued"}</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-100 border border-blue-300" /> {t.cal_generating || "Generating"}</span>
        <span className="flex items-center gap-1.5"><Image className="w-3 h-3" /> {t.cal_has_images || "Has AI images"}</span>
      </div>
    </div>
  );
}

function generateDemoArticles(year, month, t) {
  const topics = [
    { title: t.demo_art1 || "7 Ways AI is Changing Local SEO in 2026", keyword: "ai local seo 2026", volume: "12,300" },
    { title: t.demo_art2 || "How to Optimize Your Website for ChatGPT", keyword: "optimize for chatgpt", volume: "8,100" },
    { title: t.demo_art3 || "Voice Search SEO: Complete Guide", keyword: "voice search seo", volume: "5,900" },
    { title: t.demo_art4 || "Zero-Click Search: What It Means for Your Business", keyword: "zero click search", volume: "3,400" },
    { title: t.demo_art5 || "AI Content vs Human Content: What Ranks Better?", keyword: "ai vs human content seo", volume: "4,200" },
    { title: t.demo_art6 || "E-E-A-T Signals: How to Build Authority in 2026", keyword: "eeat signals seo", volume: "2,800" },
    { title: t.demo_art7 || "Schema Markup: The Complete 2026 Guide", keyword: "schema markup guide", volume: "6,700" },
    { title: t.demo_art8 || "How to Get Featured in Perplexity AI Results", keyword: "perplexity seo", volume: "1,900" },
  ];

  return topics.map((topic, i) => ({
    ...topic,
    day: i * 3 + 2,
    status: i < 3 ? "done" : i < 6 ? "queued" : "generating",
    seo_score: 85 + Math.floor(Math.random() * 12),
    wordCount: 2800 + Math.floor(Math.random() * 600),
    hasImages: true,
    images: [],
  })).filter(a => a.day <= new Date(year, month + 1, 0).getDate());
}
